/**
 * Accessories API Service
 *
 * Handles all accessory-related API calls to the backend CustomerAccessoriesController.
 */

import api from '@/lib/axios';
import {
    CustomerAccessoryResponse,
    CustomerAccessoryCatalogResponse,
    CustomerAccessorySearchRequest,
    CustomerAccessoryAvailabilityRequest,
    CustomerAccessoryAvailabilityResponse,
} from '@/types';

/**
 * Get accessories catalog (only visible accessories)
 */
export const getAccessories = async (
    params: CustomerAccessorySearchRequest
): Promise<CustomerAccessoryCatalogResponse> => {
    const response = await api.get<CustomerAccessoryCatalogResponse>(
        '/customer/accessories',
        { params }
    );
    return response.data;
};

/**
 * Get a single accessory by ID
 */
export const getAccessory = async (
    id: string,
    locationId?: string
): Promise<CustomerAccessoryResponse> => {
    const response = await api.get<CustomerAccessoryResponse>(
        `/customer/accessories/${id}`,
        { params: { locationId } }
    );
    return response.data;
};

/**
 * Get suggested accessories for an equipment type (for cross-selling)
 */
export const getAccessoriesForEquipmentType = async (
    equipmentTypeId: string,
    locationId?: string,
    take: number = 10
): Promise<CustomerAccessoryResponse[]> => {
    const response = await api.get<CustomerAccessoryResponse[]>(
        `/customer/accessories/for-equipment-type/${equipmentTypeId}`,
        { params: { locationId, take } }
    );
    return response.data;
};

/**
 * Check availability and get pricing for an accessory
 */
export const checkAccessoryAvailability = async (
    id: string,
    data: CustomerAccessoryAvailabilityRequest
): Promise<CustomerAccessoryAvailabilityResponse> => {
    const response = await api.post<CustomerAccessoryAvailabilityResponse>(
        `/customer/accessories/${id}/check-availability`,
        data
    );
    return response.data;
};

export default {
    getAccessories,
    getAccessory,
    getAccessoriesForEquipmentType,
    checkAccessoryAvailability,
};
